<template>
  <section class="kpi-subscriptions">
    <v-tabs v-model="tab" background-color="transparent">
      <v-tab>View All</v-tab>
      <v-tab>View Subscribed</v-tab>
    </v-tabs>
    <v-divider />
    <v-tabs-items v-model="tab">
      <v-tab-item style="position: relative">
        <table>
          <tr>
            <th>Handler</th>
            <th>Handler Target</th>
            <th>Counter Name</th>
            <th>Dimensions</th>
            <th>Status</th>
            <th>Value</th>
            <th>Secondary Value</th>
          </tr>
          <tr
            v-for="counter in counters"
            :key="counter.id"
            :class="{
              subscribed: getSubscribedCounters[counter.counterTypeId]
            }"
            @click.stop="() => toggleSubscription(counter)"
          >
            <td>
              {{ getCounterHandler(counter) }}
            </td>
            <td>
              {{ getTargetName(counter) }}
            </td>
            <td>
              {{ counter.counterTypeName }}
            </td>
            <td>
              <span v-if="counter.dimensions">
                <v-tooltip v-for="dimension in counter.dimensions" :key="dimension" top>
                  <template v-slot:activator="{ on }">
                    <div
                      class="dimension"
                      :class="{ subscribed: getDimensionValue(counter, dimension) !== undefined }"
                      v-on="on"
                      @click.stop="() => subscribeDimension(counter, dimension)"
                    >
                      {{ getDimensionValue(counter, dimension) }}
                    </div>
                  </template>
                  {{ getDimensionName(dimension) }}
                </v-tooltip>
              </span>
            </td>
            <td>
              <div v-if="false">
                <v-progress-circular :width="2" :size="14" color="#4caf50" indeterminate />
              </div>
              <div v-else>{{ getSubscribedCounters[counter.counterTypeId] ? 'Subscribed' : 'Not Subscribed' }}</div>
            </td>
            <td>
              {{
                Object.values(
                  getSubscribedCounters[counter.counterTypeId] ? getSubscribedCounters[counter.counterTypeId] : {}
                )
                  .map(({ value }) => value)
                  .join(', ')
              }}
            </td>
            <td>
              {{
                Object.values(
                  getSubscribedCounters[counter.counterTypeId] ? getSubscribedCounters[counter.counterTypeId] : {}
                )
                  .map(({ secondaryValue }) => secondaryValue)
                  .join(', ')
              }}
            </td>
          </tr>
        </table>
      </v-tab-item>
      <v-tab-item style="min-height: calc(100vh - 160px)">
        <SingleKPICounterBox
          v-for="counter in subscribedCounters"
          :key="counter.counterTypeId + counter.handlerId + counter.dimensionId"
          :counter-type-id="counter.counterTypeId"
          :handler-id="counter.handlerId"
          :dimension-id="counter.dimensionId"
        />
      </v-tab-item>
    </v-tabs-items>
  </section>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { DIMENSIONS } from '@/constants/statistics/constants';
import SingleKPICounterBox from '@/pages/ecs-kpi-subscriptions/SingleKPICounterBox';

export default {
  name: 'EcsKpiSubscriptions',

  components: { SingleKPICounterBox },

  data() {
    return {
      tab: null
    };
  },

  computed: {
    ...mapGetters('AGENT', ['loggedInAgent']),

    ...mapGetters('BUSINESS_PROCESSES', ['availableBusinessProcesses']),

    ...mapGetters('STATS', ['getSubscribedCounters']),

    ...mapGetters('SYSTEM', ['interactionTypes']),

    counters() {
      return (this.loggedInAgent.organizationData.counters || []).sort(
        (a, b) => a.counterTypeHandler - b.counterTypeHandler
      );
    },

    subscribedCounters() {
      const subscriptions = [];

      Object.keys(this.getSubscribedCounters).forEach(counterTypeId => {
        Object.keys(this.getSubscribedCounters[counterTypeId]).forEach(handlerId => {
          if (this.getSubscribedCounters[counterTypeId][handlerId].refCount) {
            subscriptions.push({ counterTypeId, handlerId });
          }
          Object.keys(this.getSubscribedCounters[counterTypeId][handlerId]).forEach(dimensionId => {
            if (dimensionId !== 'refCount' && dimensionId !== 'value') {
              subscriptions.push({ counterTypeId, handlerId, dimensionId });
            }
          });
        });
      });

      return subscriptions;
    }
  },

  methods: {
    ...mapActions('STATS', ['addKpiSubscription']),

    getBusinessProcessName(businessProcessId) {
      const bp = this.availableBusinessProcesses.find(({ id }) => id === businessProcessId);
      return bp && bp.name;
    },

    subscribeDimension(counter, dimensionId) {
      const type = this.getCounterHandler(counter);

      if (type === 'Agent') {
        this.addKpiSubscription({
          counterTypeId: counter.counterTypeId,
          handlerId: this.loggedInAgent.id,
          dimensionId
        });
      } else {
        this.$root.$emit('open-business-process-selection-dialog', {
          callback: businessProcessId => {
            this.addKpiSubscription({
              counterTypeId: counter.counterTypeId,
              handlerId: businessProcessId,
              dimensionId
            });
          }
        });
      }
    },

    toggleSubscription(counter) {
      const type = this.getCounterHandler(counter);

      if (type === 'Agent') {
        this.addKpiSubscription({
          counterTypeId: counter.counterTypeId,
          handlerId: this.loggedInAgent.id
        });
      } else {
        this.$root.$emit('open-business-process-selection-dialog', {
          callback: businessProcessId => {
            this.addKpiSubscription({
              counterTypeId: counter.counterTypeId,
              handlerId: businessProcessId
            });
          }
        });
      }
    },

    getTargetName(counter) {
      const storedCounter = Object.keys(this.getSubscribedCounters[counter.counterTypeId] || {});
      let name = '';

      const getName = handlerId => {
        if (!handlerId) {
          return null;
        }

        const type = this.getCounterHandler(counter);

        switch (type) {
          case 'Business Process':
            return this.getBusinessProcessName(handlerId);
          case 'Agent':
            return this.loggedInAgent.name;
          default:
            return 'Unknown';
        }
      };

      Object.values(storedCounter || {}).forEach(handlerId => {
        name += name ? `, ${getName(handlerId)}` : getName(handlerId);
      });

      return name;
    },

    getCounterHandler(counter) {
      if (!counter.counterTypeHandler) {
        return 'Unknown';
      }
      return DIMENSIONS.counterTypeHandler[parseInt(counter.counterTypeHandler)] || counter.counterTypeHandler;
    },

    getDimensionName(dimensionId) {
      const interactionType = this.interactionTypes.find(({ id }) => id === dimensionId);
      return interactionType && interactionType.name;
    },

    getDimensionValue(counter, dimensionId) {
      const storedCounter = this.getSubscribedCounters[counter.counterTypeId] || {};
      const handlerId = Object.keys(storedCounter)[0];
      const dimensions = storedCounter[handlerId] || {};

      return dimensions[dimensionId] && dimensions[dimensionId].value;
    }
  }
};
</script>

<style scoped>
.kpi-subscriptions {
  padding: 0 0 20px 0;
  min-height: calc(100vh - 64px - 56px);
}
table {
  width: 100%;
  border-collapse: collapse;
  opacity: 0.9;
  position: relative;
}
tr.subscribed {
  color: #4caf50;
}
tr:nth-child(even):after {
  display: block;
  width: 100%;
  height: 100%;
  background-color: yellow;
}
tr:hover {
  background-color: var(--v-surface-base);
  cursor: pointer;
  user-select: none;
}
td {
  padding: 0 20px;
}
th {
  top: 0;
  position: sticky;
  padding: 2px 20px;
  text-align: left;
  background-color: var(--v-surface-darken2);
  color: #fff;
  border-bottom: 1px solid #444;
}
th:hover {
  background-color: var(--v-surface-darken2);
}
.dimension {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #4caf50;
  border-radius: 8px;
  margin: 2px 2px 0;
}
.dimension.subscribed {
  text-align: center;
  min-width: 20px;
  width: auto;
  height: auto;
  padding: 0 2px;
  font-size: 10px;
  background-color: #4caf50;
  color: #fff;
}
</style>
